/* global JSX */
import downloadjs from "downloadjs";
import * as attachmentService from "source/models/services/attachmentService";
import * as React from "react";
import { FileAttachment, IFileAttachment } from "ui/Scripts/source/models/common/fileAttachment";
import { AttachmentImageSize, ConfirmationDialogResult } from "ui/Scripts/source/models/enums";
import { Translations } from "ui/Scripts/source/models/translations";
import { AttachmentList, AttachmentListDirection } from "./AttachmentList";
import { useEffect, useState } from "react";
import { Attachments } from "ui/Scripts/source/models/attachment/attachments";
import { AppUtils } from "source/framework/appUtils";
import { Base } from "ui/Scripts/source/framework/base";
import { SaveDataObj } from "source/framework/saveData";
import { ApiSuccess } from "ui/Scripts/source/models/common/apiSuccess";
import { SaveAttachmentBase64 } from "ui/Scripts/source/models/common/saveAttachmentBase64";
import { SaveSuccessItems } from "ui/Scripts/source/models/common/saveSuccessItems";
import { EditItem } from "ui/Scripts/source/models/common/editItem";
import { LocalLoading } from "source/components/framework/Loading/LocalLoading";

interface IAttachmentListOfRecordProp {
    companyId: string;
    recordId: string;
    dataId?: string;
    accept?: string;
    category?: number;
    direction?: AttachmentListDirection;
    hideThumbnails?: boolean;
    ignoreAspectRatio?: boolean;
    imageSize?: AttachmentImageSize;
    isNewMessage?: string;
    isReadOnly?: boolean;
    lateThreshold?: number;
    margin?: string | number;
    size?: "sm" | "md",
    title: string | JSX.Element;
    onGetAttachments: (companyId: string, id: string) => Promise<Attachments>;
    onRemoveAttachments: (data: any) => Promise<ApiSuccess>;
    onAddAttachments: (data: any) => Promise<SaveSuccessItems>;
    onIsLoadingChange?: (isLoading: boolean) => void;
}

interface ICalendarTaskListOfRecordListState {
    attachments: IFileAttachment[];
}

export const AttachmentListOfRecord = (props: IAttachmentListOfRecordProp) => {
    const [isLoading, setIsLoading] = useState(false);
    
    useEffect(() => {
        if (!props.onIsLoadingChange) return;
        props.onIsLoadingChange(isLoading);
    }, [isLoading]);

    const [state, setState] = useState<ICalendarTaskListOfRecordListState>({
        attachments: [],
    });

    const loadAttachments = async (companyId: string, recordId: string) => {
        const isNewRecord = props.recordId === Base.emptyGuid;
        if (isNewRecord) {
            return;
        }
        setIsLoading(true);
        const result = await AppUtils.callService(() => props.onGetAttachments(companyId, recordId), false, true, () => setIsLoading(false));
        if (!result) return;
        setState((old) => ({
            ...old,
            attachments: result.attachments
        }));
    };

    useEffect(() => {
        if (!props.companyId || !props.recordId) return;
        loadAttachments(props.companyId, props.recordId);
    }, [props.companyId, props.recordId, props.dataId ?? ""]);

    const handleAttachmentDownload = async (id: string) => {
        if (!id) return;
        const attachment = state.attachments.find(i => i.id === id);
        if (!attachment) return;
        if (attachment.file) {
            downloadjs(attachment.file, attachment.name);
            return;
        }
        if (attachment.isNew()) return;
        const fileName = await AppUtils.callService(() => attachmentService.getAttachment(id));
        if (!fileName) return;
        AppUtils.showSuccessMessage(String.format(Translations.AttachmentParameterDownloaded, fileName));
    };

    const getRemoveAttachmentsSaveData = (category: number, attachmentIds: string[]): SaveDataObj => {
        const result = new SaveDataObj();
        result.append("id", props.recordId);
        result.append("companyId", props.companyId);
        result.append("subOwnerId", null);
        result.append("category", category);
        result.append("ids", attachmentIds);
        return result;
    };

    const handleAttachmentRemove = async (category: number, id: string) => {
        if (!id) return;
        if (await AppUtils.showConfirmationDialog(String.format(Translations.DoYouReallyWantToRemoveAttachmentParameter, state.attachments.find(x => x.id === id)?.name), false) === ConfirmationDialogResult.No) return;
        const saveData = getRemoveAttachmentsSaveData(category, [id]);
        const result = await AppUtils.callService(() => props.onRemoveAttachments(saveData.obj));
        if (!result) return;
        setState(old => ({
            ...old,
            attachments: old.attachments.filter(i => i.id !== id)
        }));
    };

    const getAddAttachmentsSaveData = async (category: number, attachments: IFileAttachment[]): Promise<SaveDataObj> => {
        const result = new SaveDataObj();
        result.append("id", props.recordId);
        result.append("companyId", props.companyId);
        result.append("subOwnerId", null);
        result.append("category", category);
        result.append("attachments", await SaveAttachmentBase64.fromFileAttachments(attachments.filter(i => !!i.file)));
        return result;
    };
    
    const handleAttachmentsAdd = async (category: number, fileList: FileList) => {
        const attachments = await FileAttachment.addFileListToFileAttachments([], fileList, category);
        if (attachments.length < 1) return;
        const saveData = await getAddAttachmentsSaveData(category, attachments);
        const result = await AppUtils.callService(() => props.onAddAttachments(saveData.obj));
        if (!result) return;
        EditItem.setItemIdsFromIds(attachments, result.ids);
        setState(old => ({
            ...old,
            attachments: old.attachments.concat(attachments)
        }));
    };

    const category = props.category ?? 0;

    return (
        <>
            {(!props.onIsLoadingChange && isLoading) && (
                <LocalLoading />
            )}
            <AttachmentList
                accept={props.accept}
                attachments={state.attachments.filter(i => i.category === category)}
                direction={props.direction}
                hideThumbnails={props.hideThumbnails}
                ignoreAspectRatio={props.ignoreAspectRatio}
                imageSize={props.imageSize}
                isNewMessage={props.isNewMessage}
                isReadOnly={props.isReadOnly}
                lateThreshold={props.lateThreshold}
                margin={props.margin}
                size={props.size}
                title={props.title}
                onRemoveAttachment={(id) => handleAttachmentRemove(category, id)}
                onDownloadAttachment={handleAttachmentDownload}
                onAddAttachments={(fileList) => handleAttachmentsAdd(category, fileList)}
            />
        </>
    );
};